import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  MapPin, Clock, Star, Check, X, Download, Loader2, Calendar, Users,
  Hotel, Camera, Car, Utensils, FileText, Plane,
} from "lucide-react";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import { formatINR } from "@/mock";
import { apiGetHoliday, apiHolidayEnquiry } from "@/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const INC_ICON = {
  "Return Flights": Plane, "Hotel Stay": Hotel, "Airport Transfers": Car,
  "Daily Breakfast": Utensils, Sightseeing: Camera, "Visa Assistance": FileText,
};

const PackageDetail = () => {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [hero, setHero] = useState(0);
  const [tab, setTab] = useState("overview");
  const [enq, setEnq] = useState({ name: "", email: "", phone: "", travelDate: "", pax: 2, message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    apiGetHoliday(slug).then((d) => { setP(d); setHero(0); }).catch(() => setErr(true));
  }, [slug]);

  const submit = async () => {
    if (!enq.name || !enq.email || !enq.phone) return toast.error("Please fill name, email & phone");
    setBusy(true);
    try {
      await apiHolidayEnquiry({ packageId: p.package_id, ...enq, pax: Number(enq.pax) || 2 });
      toast.success("Enquiry sent! Our holiday expert will call you shortly.");
      setEnq({ name: "", email: "", phone: "", travelDate: "", pax: 2, message: "" });
    } catch { toast.error("Could not submit enquiry"); }
    finally { setBusy(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-[#F5F7FB]"><TopBar /><Header />
      <p className="py-32 text-center text-sm text-slate-400" data-testid="pkg-not-found">Package not found. <a href="/package" className="font-bold text-[#0E7C6B]">Browse all packages</a></p><Footer /></div>
  );
  if (!p) return (
    <div className="flex min-h-screen items-center justify-center bg-[#F5F7FB]"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>
  );

  const gallery = p.gallery?.length ? p.gallery : [p.image];
  const pdfUrl = `${BACKEND}/api/holidays/${p.slug || p.package_id}/itinerary.pdf`;

  return (
    <div className="min-h-screen bg-[#F5F7FB]" style={{ fontFamily: "'Kumbh Sans', sans-serif" }} data-testid="package-detail">
      <TopBar />
      <Header />

      {/* Title bar */}
      <div className="border-b border-slate-100 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-[22px] font-bold text-[#1B1F5B] md:text-[26px]" style={{ fontFamily: "'Overlock', cursive" }} data-testid="pkg-title">{p.title}</h1>
              <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs font-semibold text-slate-500">
                <span className="flex items-center gap-1 text-[#0E7C6B]"><MapPin className="h-3.5 w-3.5" /> {p.destination}, {p.country}</span>
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {p.nights}N / {p.days}D</span>
                <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-[#F5B32B] text-[#F5B32B]" /> {p.rating}</span>
                <span className="rounded-full bg-[#1B1F5B]/5 px-2.5 py-0.5 text-[#1B1F5B]">{p.category}</span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-slate-400">Starting from</p>
              <p className="text-2xl font-extrabold text-[#1B1F5B]" data-testid="pkg-price">{formatINR(p.price)}<span className="text-xs font-medium text-slate-400"> /person</span></p>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery */}
      <div className="mx-auto max-w-7xl px-4 py-5">
        <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
          <div className="relative h-64 overflow-hidden rounded-2xl md:h-[26rem]">
            <img src={gallery[hero]} alt={p.title} className="h-full w-full object-cover" data-testid="pkg-hero-img" />
            <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="pkg-download-itinerary"
              className="absolute bottom-4 right-4 flex items-center gap-2 rounded-xl bg-white/95 px-4 py-2.5 text-xs font-extrabold text-[#1B1F5B] shadow-lg transition-transform hover:scale-105">
              <Download className="h-4 w-4" /> Download Itinerary
            </a>
          </div>
          <div className="grid grid-cols-3 gap-3 md:grid-cols-2">
            {gallery.slice(0, 6).map((g, i) => (
              <button key={i} onClick={() => setHero(i)} data-testid={`pkg-thumb-${i}`}
                className={`h-20 overflow-hidden rounded-xl md:h-[8.3rem] ${hero === i ? "ring-2 ring-[#F5B32B]" : ""}`}>
                <img src={g} alt="" className="h-full w-full object-cover transition-transform hover:scale-110" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-20 border-y border-slate-100 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4">
          {["overview", "itinerary", "inclusions", "terms"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`pkg-tab-${t}`}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-bold capitalize transition-colors ${tab === t ? "border-[#F5B32B] text-[#1B1F5B]" : "border-transparent text-slate-400 hover:text-slate-600"}`}>{t}</button>
          ))}
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-6 px-4 py-6 lg:grid-cols-[1fr_340px]">
        <div>
          {tab === "overview" && (
            <div data-testid="pkg-overview">
              <h2 className="text-[24px] font-bold text-[#1B1F5B] md:text-[28px]" style={{ fontFamily: "'Overlock', cursive" }}>{p.title} Overview</h2>
              <p className="mt-2 whitespace-pre-line text-[15px] leading-[1.7] text-slate-600">{p.overview || p.summary}</p>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[["Hotel", Hotel], ["Sightseeing", Camera], ["Transfer", Car], ["Activity", Check]].map(([l, I]) => (
                  <div key={l} className="flex flex-col items-center gap-1 rounded-xl border border-slate-100 bg-white py-4 text-xs font-bold text-slate-600">
                    <I className="h-5 w-5 text-[#0E7C6B]" /> {l}
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "itinerary" && (
            <div data-testid="pkg-itinerary">
              <h2 className="mb-4 text-[24px] font-bold text-[#1B1F5B] md:text-[28px]" style={{ fontFamily: "'Overlock', cursive" }}>Day-wise Itinerary</h2>
              <div className="space-y-5">
                {(p.itinerary || []).map((d) => (
                  <div key={d.day} className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm" data-testid={`pkg-day-${d.day}`}>
                    {d.image && <img src={d.image} alt={d.title} className="h-48 w-full object-cover md:h-60" loading="lazy" />}
                    <div className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="rounded-lg bg-[#F5B32B] px-2.5 py-1 text-sm font-semibold text-[#1B1F5B]">Day {d.day}</span>
                        <h3 className="text-sm font-semibold text-[#1B1F5B]">{d.title}</h3>
                      </div>
                      <p className="mt-2 whitespace-pre-line text-sm leading-[1.7] text-slate-600">{d.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "inclusions" && (
            <div className="grid gap-5 sm:grid-cols-2" data-testid="pkg-inclusions">
              <div>
                <h3 className="mb-2 text-lg font-bold text-[#0E7C6B]">Inclusions</h3>
                <div className="space-y-1.5">
                  {(p.inclusions_detail || p.inclusions || []).map((i, k) => (
                    <div key={k} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0E7C6B]" /> {i}</div>
                  ))}
                </div>
              </div>
              {p.exclusions?.length > 0 && (
                <div>
                  <h3 className="mb-2 text-lg font-bold text-rose-500">Exclusions</h3>
                  <div className="space-y-1.5">
                    {p.exclusions.map((i, k) => (
                      <div key={k} className="flex items-start gap-2 text-sm text-slate-600"><X className="mt-0.5 h-4 w-4 shrink-0 text-rose-400" /> {i}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === "terms" && (
            <div className="space-y-4 text-sm leading-[1.7] text-slate-600" data-testid="pkg-terms">
              <div>
                <h3 className="mb-1 text-lg font-bold text-[#1B1F5B]">Payment Policy</h3>
                <p>30 days or more before departure: 25% of total cost · 29–20 days: 50% · 19 days or less: 100% of total cost.</p>
              </div>
              <div>
                <h3 className="mb-1 text-lg font-bold text-[#1B1F5B]">Cancellation Policy</h3>
                <p>Cancellations must be sent in writing/email from the person who made the booking. Charges apply as per the published cancellation policy and supplier rules.</p>
              </div>
              <div>
                <h3 className="mb-1 text-lg font-bold text-[#1B1F5B]">Terms &amp; Conditions</h3>
                <p>Arrangements are planned in advance. Occasionally changes may be necessary and we reserve the right to make them, advising you at the earliest. We also reserve the right to cancel arrangements with reasons assigned.</p>
              </div>
            </div>
          )}
        </div>

        {/* Enquiry card */}
        <div className="h-fit rounded-2xl border border-slate-100 bg-white p-5 shadow-sm lg:sticky lg:top-16" data-testid="pkg-enquiry">
          <h3 className="text-lg font-bold text-[#1B1F5B]" style={{ fontFamily: "'Overlock', cursive" }}>Want to go for a memorable holiday?</h3>
          <p className="mb-3 mt-1 text-[11px] text-slate-400">Share your details to know the best holiday deals</p>
          <div className="space-y-2.5">
            <input data-testid="pkg-enq-name" value={enq.name} onChange={(e) => setEnq({ ...enq, name: e.target.value })} placeholder="Your name" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
            <input data-testid="pkg-enq-email" type="email" value={enq.email} onChange={(e) => setEnq({ ...enq, email: e.target.value })} placeholder="Email" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
            <input data-testid="pkg-enq-phone" value={enq.phone} onChange={(e) => setEnq({ ...enq, phone: e.target.value })} placeholder="Mobile number" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
            <div className="grid grid-cols-2 gap-2">
              <div className="relative"><Calendar className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-slate-400" /><input data-testid="pkg-enq-date" type="date" value={enq.travelDate} onChange={(e) => setEnq({ ...enq, travelDate: e.target.value })} className="w-full rounded-xl border border-slate-200 py-2.5 pl-8 pr-2 text-xs outline-none focus:border-[#0E7C6B]" /></div>
              <div className="relative"><Users className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-slate-400" /><input data-testid="pkg-enq-pax" type="number" min={1} value={enq.pax} onChange={(e) => setEnq({ ...enq, pax: e.target.value })} className="w-full rounded-xl border border-slate-200 py-2.5 pl-8 pr-2 text-sm outline-none focus:border-[#0E7C6B]" /></div>
            </div>
            <textarea data-testid="pkg-enq-message" value={enq.message} onChange={(e) => setEnq({ ...enq, message: e.target.value })} rows={2} placeholder="Any preferences? (optional)" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
            <button data-testid="pkg-enq-submit" onClick={submit} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#F5B32B] py-3 text-sm font-extrabold text-[#0A0F2E] hover:bg-[#E89A10] disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Enquiry"}
            </button>
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#1B1F5B]/20 py-2.5 text-xs font-bold text-[#1B1F5B] hover:bg-[#1B1F5B]/5">
              <Download className="h-3.5 w-3.5" /> Download Itinerary PDF
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PackageDetail;
